/**
 * RequestDetailContent — what one request-log row holds (TRA-1412).
 *
 * The body of the request-detail sheet: routing facts, the params, tools
 * and messages the gateway forwarded, the parsed response, and the two raw
 * bodies folded at the bottom. It takes a loaded document and nothing
 * else, so a test renders it with a made-up document and no dialog or
 * query context. `RequestDetailSheet` owns the loading.
 *
 * A later turn of a conversation holds only the messages the gateway saved
 * for it (`messages[fromIndex:]`). The content says how many earlier ones
 * exist and links to the whole-run page rather than rebuilding the
 * history here.
 */

import { useMemo, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { TokenUsage, TraceDocument } from '@/api/generated/model'
import { statusVariant, traceIdFromKey } from '@/components/models/home/requestLogRow'
import { useConversationLinkState } from '@/components/traces/conversationLink'
import {
  CLIP_LENGTH,
  earlierMessageCount,
  formatRawBody,
  parseRequestBody,
  parseResponseBody,
  wholeRunPath,
  type ParamEntry,
  type ParsedRequestBody,
  type ParsedResponseBody,
  type RequestBodyState,
  type ResponseBodyState,
  type ToolSchema,
} from '@/components/models/home/requestDetail'
import type { RawMessage, RawToolCall } from '@/components/review/transcript'
import { ImageThumb } from '@/components/review/TrajectoryTranscript'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { CopyButton } from '@/components/ui/copy-button'

const LABEL = 'font-mono text-xs uppercase tracking-wider text-muted-foreground'
/** Data-role text; the sheet chrome uses it for the trace id too. */
export const MONO = 'break-all font-mono text-xs text-foreground'
const PRE = 'overflow-x-auto whitespace-pre-wrap break-all border border-border bg-muted/30 p-3 font-mono text-xs'

function SectionTitle({ children }: { children: string }): React.JSX.Element {
  return <h3 className="font-serif text-base text-foreground">{children}</h3>
}

/** A mono-uppercase label over a data value. */
function Fact({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="space-y-0.5">
      <dt className={LABEL}>{label}</dt>
      <dd className={MONO}>{children}</dd>
    </div>
  )
}

function FoldTrigger({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <CollapsibleTrigger className="group flex items-center gap-1 text-left">
      <ChevronRight
        className="h-3.5 w-3.5 text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
        aria-hidden="true"
      />
      {children}
    </CollapsibleTrigger>
  )
}

/** Long text is clipped until the reader asks for all of it. */
function ClippedText({ text }: { text: string }): React.JSX.Element {
  const [showAll, setShowAll] = useState(false)
  const clipped = !showAll && text.length > CLIP_LENGTH
  return (
    <div className="space-y-2">
      <p className="whitespace-pre-wrap break-words text-sm text-foreground">
        {clipped ? `${text.slice(0, CLIP_LENGTH)}…` : text}
      </p>
      {clipped ? (
        <Button type="button" size="sm" variant="outline" onClick={() => { setShowAll(true) }}>
          Show all
        </Button>
      ) : null}
    </div>
  )
}

function ToolCallList({ calls }: { calls: RawToolCall[] }): React.JSX.Element | null {
  if (calls.length === 0) return null
  return (
    <ul className="space-y-2">
      {calls.map((call, index) => (
        <li key={call.id ?? index} className="space-y-1">
          <p className={LABEL}>
            Tool call {call.name}
            {call.id === null ? null : <span className="ml-2 normal-case">{call.id}</span>}
          </p>
          <pre className={PRE}>{formatRawBody(call.args)}</pre>
        </li>
      ))}
    </ul>
  )
}

function MessageCard({ message }: { message: RawMessage }): React.JSX.Element {
  return (
    <li className="space-y-2 border border-border p-3">
      <p className={LABEL}>
        {message.role}
        {message.toolCallId === null ? null : (
          <span className="ml-2 normal-case">{message.toolCallId}</span>
        )}
      </p>
      {message.reasoning === null ? null : (
        <div className="space-y-1">
          <p className={LABEL}>Reasoning</p>
          <ClippedText text={message.reasoning} />
        </div>
      )}
      {message.content === '' ? null : <ClippedText text={message.content} />}
      {message.images.length === 0 ? null : (
        <div className="flex flex-wrap gap-2">
          {message.images.map((url, index) => (
            <ImageThumb key={index} url={url} index={index} />
          ))}
        </div>
      )}
      <ToolCallList calls={message.toolCalls} />
    </li>
  )
}

function ParamList({ params }: { params: ParamEntry[] }): React.JSX.Element | null {
  if (params.length === 0) return null
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
      {params.map((param) => (
        <div key={param.key} className="contents">
          <dt className={MONO}>{param.key}</dt>
          <dd className="break-all font-mono text-xs text-muted-foreground">{param.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function ToolSchemaList({ tools }: { tools: ToolSchema[] }): React.JSX.Element | null {
  if (tools.length === 0) return null
  return (
    <div className="space-y-1">
      <p className={LABEL}>Tools</p>
      <ul className="space-y-1">
        {tools.map((tool, index) => (
          <li key={`${tool.name}-${index.toString()}`}>
            <Collapsible>
              <FoldTrigger>
                <span className={MONO}>{tool.name}</span>
              </FoldTrigger>
              <CollapsibleContent>
                <pre className={`${PRE} mt-1`}>{tool.schema}</pre>
              </CollapsibleContent>
            </Collapsible>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The one line a body gets when there is nothing readable to show. */
function BodyNote({ state }: { state: RequestBodyState | ResponseBodyState }): React.JSX.Element | null {
  return (
    <>
      {state.kind === 'missing' ? (
        <p className="text-sm text-muted-foreground">
          Not saved. Tracing was off, or the body was over 10 MB.
        </p>
      ) : null}
      {state.kind === 'raw' ? <p className="text-sm text-muted-foreground">Shown raw below.</p> : null}
      {state.truncated ? (
        <p className="text-sm text-muted-foreground">This body went over 10 MB and was cut off.</p>
      ) : null}
    </>
  )
}

function EarlierMessagesNote({
  doc,
  traceKey,
  pageName,
}: {
  doc: TraceDocument
  traceKey: string
  pageName: string
}): React.JSX.Element | null {
  const earlier = earlierMessageCount(doc)
  const runPath = wholeRunPath(doc, traceKey)
  const linkState = useConversationLinkState(pageName)
  if (earlier === 0 && runPath === null) return null
  return (
    <div className="space-y-1 border border-border p-3 text-sm">
      {earlier > 0 ? (
        <p className="text-foreground">
          This call also sent {earlier.toLocaleString()} earlier{' '}
          {earlier === 1 ? 'message' : 'messages'}. They are saved with the earlier calls of
          this run.
        </p>
      ) : null}
      {runPath === null ? null : (
        <Link to={runPath} state={linkState} className="text-foreground underline underline-offset-2">
          Open the whole run
        </Link>
      )}
    </div>
  )
}

function RequestSection({ body }: { body: ParsedRequestBody }): React.JSX.Element {
  return (
    <>
      <div className="space-y-1">
        <p className={LABEL}>Params</p>
        {body.params.length === 0 ? (
          <p className="text-sm text-muted-foreground">No params were sent beyond the messages and tools.</p>
        ) : (
          <ParamList params={body.params} />
        )}
      </div>
      <ToolSchemaList tools={body.tools} />
      <div className="space-y-1">
        <p className={LABEL}>Messages</p>
        <ul className="space-y-2">
          {body.messages.map((message, index) => (
            <MessageCard key={index} message={message} />
          ))}
        </ul>
      </div>
    </>
  )
}

function ResponseSection({ body }: { body: ParsedResponseBody }): React.JSX.Element {
  const { message } = body
  return (
    <>
      {message.reasoning === null ? null : (
        <div className="space-y-1">
          <p className={LABEL}>Reasoning</p>
          <ClippedText text={message.reasoning} />
        </div>
      )}
      {message.content === '' ? null : <ClippedText text={message.content} />}
      <ToolCallList calls={message.toolCalls} />
      <dl>
        <Fact label="Finish reason">{body.finishReason ?? 'not reported'}</Fact>
      </dl>
    </>
  )
}

/** Pretty-printed only once mounted, so a folded body costs nothing. */
function RawText({ text }: { text: string }): React.JSX.Element {
  const pretty = useMemo(() => formatRawBody(text), [text])
  return <pre className={`${PRE} mt-2`}>{pretty}</pre>
}

function RawBody({ title, text }: { title: string; text: string | null | undefined }): React.JSX.Element {
  const [open, setOpen] = useState(false)
  return (
    <section className="space-y-1">
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="flex items-center justify-between gap-2">
          <FoldTrigger>
            <SectionTitle>{title}</SectionTitle>
          </FoldTrigger>
          {text == null ? null : <CopyButton value={text} label={`Copy ${title.toLowerCase()}`} />}
        </div>
        <CollapsibleContent>
          {text == null ? (
            <p className="mt-2 text-sm text-muted-foreground">This body was not saved.</p>
          ) : (
            <RawText text={text} />
          )}
        </CollapsibleContent>
      </Collapsible>
    </section>
  )
}

function tokenLine(usage: TokenUsage | null | undefined): string {
  if (usage == null) return 'not reported'
  return (
    `${usage.promptTokens.toLocaleString()} prompt, ` +
    `${usage.completionTokens.toLocaleString()} completion, ` +
    `${usage.totalTokens.toLocaleString()} total`
  )
}

function RoutingSection({ doc }: { doc: TraceDocument }): React.JSX.Element {
  return (
    <section className="space-y-2">
      <SectionTitle>Routing</SectionTitle>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Fact label="Upstream">{doc.upstreamId}</Fact>
        {doc.fallbackFrom == null ? null : (
          <Fact label="Fallback">primary {doc.fallbackFrom} failed</Fact>
        )}
        <Fact label="Request model">{doc.requestModel ?? '—'}</Fact>
        <Fact label="Actual model">{doc.actualModel ?? '—'}</Fact>
        <Fact label="Status">{doc.statusCode}</Fact>
        <Fact label="Latency">{doc.latencyMs.toLocaleString()} ms</Fact>
        <Fact label="Streaming">{doc.isStreaming ? 'yes' : 'no'}</Fact>
        <Fact label="Tokens">{tokenLine(doc.tokenUsage)}</Fact>
        {doc.errorMessage == null ? null : (
          <div className="col-span-2 space-y-0.5">
            <dt className={LABEL}>Error</dt>
            <dd className="break-words text-sm text-destructive">{doc.errorMessage}</dd>
          </div>
        )}
      </dl>
    </section>
  )
}

function CallHeader({ doc, traceKey }: { doc: TraceDocument; traceKey: string }): React.JSX.Element {
  const traceId = traceIdFromKey(traceKey)
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant={statusVariant(doc.statusCode)} className="font-mono">
        {doc.statusCode}
      </Badge>
      <span className={MONO}>{traceId}</span>
      <CopyButton value={traceId} label="Copy trace id" />
    </div>
  )
}

/** Everything below the sheet chrome, for one loaded document. */
export function RequestDetailContent({
  doc,
  traceKey,
  pageName,
}: {
  doc: TraceDocument
  traceKey: string
  /** The page the sheet is on, for the whole run's back arrow (TRA-1412). */
  pageName: string
}): React.JSX.Element {
  const request = parseRequestBody(doc.request)
  const response = parseResponseBody(doc.response)
  return (
    <div className="space-y-6">
      <CallHeader doc={doc} traceKey={traceKey} />
      <RoutingSection doc={doc} />
      <section className="space-y-3">
        <SectionTitle>Request</SectionTitle>
        <EarlierMessagesNote doc={doc} traceKey={traceKey} pageName={pageName} />
        <BodyNote state={request} />
        {request.kind === 'request' ? <RequestSection body={request} /> : null}
      </section>
      <section className="space-y-3">
        <SectionTitle>Response</SectionTitle>
        <BodyNote state={response} />
        {response.kind === 'response' ? <ResponseSection body={response} /> : null}
      </section>
      <RawBody title="Raw request" text={doc.request.bodyText} />
      <RawBody title="Raw response" text={doc.response.bodyText} />
    </div>
  )
}
